/**
 * trial-flow.js (v7 - stays on-page in the theme editor)
 * ---------------------------------------------------------------
 * ONE product, ONE selling plan per frequency. Cycle-1 trial pricing
 * and the pack-size swap for cycle 2+ are both handled inside Appstle
 * (Free Trial setting + Product Swap Automation "after 1st billing")
 * - this file just drives the 3-step UI and, on Complete order, adds
 * exactly ONE line item: { id, quantity: 1, selling_plan }.
 *
 * Every init function is wrapped in try/catch. If anything throws,
 * a visible red debug banner is shown instead of silently leaving
 * the page half-broken (e.g. a "Get started" button stuck in its
 * default disabled state with no clue why). Remove the banner calls
 * once the flow is fully stable if you don't want them user-facing.
 *
 * Steps 2 and 3 need a real trial selection (stored in sessionStorage
 * during Step 1) to render — on the live storefront, if that's
 * missing, we send the visitor back to the homepage since they've
 * landed on the page out of order. But that same redirect used to
 * fire the moment you opened "Review your order" from Online Store >
 * Themes > Customize, since the editor has no session state either -
 * kicking you straight to the homepage before you could add/edit any
 * blocks. isThemeEditor() originally only checked window.Shopify.
 * designMode, which is true inside the Customize editor iframe — but
 * stays FALSE when you use the plain admin "Preview" link/button (or
 * open that preview URL on another device/browser), so Preview always
 * bounced to the homepage the same way a real out-of-order visitor
 * would. It now also treats any non-live theme (window.Shopify.theme.
 * role !== 'main' — i.e. an unpublished/preview/demo theme) as an
 * editor-like context, so Preview shows the same inline notice instead
 * of redirecting. Only the actual published theme still redirects real
 * visitors who land out of order, which is the behaviour you want.
 */
(function () {
  'use strict';

  var STATE_KEY = 'bb_trial_flow_state';

  function getState() {
    try { return JSON.parse(sessionStorage.getItem(STATE_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function setState(patch) {
    var next = Object.assign({}, getState(), patch);
    sessionStorage.setItem(STATE_KEY, JSON.stringify(next));
    return next;
  }

  function formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  // Renders a product thumbnail as a CSS background-image on the
  // container rather than an <img> tag. The old '<img width="60">'
  // markup relied on the theme's global CSS to size/crop it, and a
  // site-wide "img { height: auto }" responsive-image rule (common in
  // most Shopify themes, sometimes !important) was overriding that and
  // stretching portrait product photos into tall, narrow slivers.
  // A background-image on a fixed-size box is immune to any img rules.
  function setMediaImage(el, url, alt) {
    if (!el || !url) return;
    el.innerHTML = '';
    el.style.backgroundImage = 'url("' + url + '")';
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', alt || '');
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  }

  /**
   * Reads the selling plan's own price_adjustments (its Appstle-defined
   * discount schedule) and works out: the price to actually show as
   * "the subscription price" (the first tier that isn't £0 - i.e. skips
   * over the free-trial cycle), and a human note about what happens after
   * that tier ends, e.g. "then £35.00 from your 4th delivery".
   * Only resolves value_type "price" tiers (an absolute fixed price per
   * cycle) since that's the only type we can compute without knowing
   * which base price percentage/fixed_amount discounts are meant to
   * apply against. Returns null if the plan has 0-1 resolvable paid
   * tiers, so callers can fall back to the old flat-price behaviour.
   */
  function getPriceSchedule(plan) {
    var adjustments = plan.priceAdjustments;
    if (!adjustments || !adjustments.length) return null;

    var tiers = [];
    var cumulativeOrders = 0;
    for (var i = 0; i < adjustments.length; i++) {
      var adj = adjustments[i];
      var priceCents;
      if (adj.valueType === 'price') {
        priceCents = Math.round(adj.value); // already in the shop's minor currency unit (pence)
      } else if (adj.valueType === 'percentage' && adj.value >= 100) {
        priceCents = 0; // 100% off = the free trial tier
      } else {
        // fixed_amount, or a percentage we can't safely resolve without
        // knowing which base price it's meant to discount off of
        return null;
      }
      tiers.push({ price: priceCents, orderCount: adj.orderCount, startsAtOrder: cumulativeOrders + 1 });
      if (adj.orderCount) cumulativeOrders += adj.orderCount;
    }

    var paidTiers = tiers.filter(function (t) { return t.price > 0; });
    if (paidTiers.length === 0) return null;

    var first = paidTiers[0];
    var next = paidTiers[1];
    var note = '';
    if (first.orderCount) {
      note = 'For your first ' + first.orderCount + ' deliver' + (first.orderCount === 1 ? 'y' : 'ies');
      if (next) {
        note += ', then ' + formatMoney(next.price) + ' from delivery ' + next.startsAtOrder + ' onward.';
      } else {
        note += '.';
      }
    } else if (next) {
      note = 'Then ' + formatMoney(next.price) + ' from delivery ' + next.startsAtOrder + ' onward.';
    }

    return { firstPrice: first.price, note: note };
  }

  function showDebug(root, label, err) {
    if (arguments.length > 2) console.error('[trial-flow] ' + label, err);
    // Without this the banners stack up one per re-render/dropdown change.
    root.querySelectorAll('.trial-flow__notice').forEach(function (n) { n.remove(); });
    var el = document.createElement('div');
    el.style.cssText = 'background:#fdeaea;border:1px solid #e0a1a1;color:#8a1f1f;' +
      'padding:12px 16px;margin:12px 0;border-radius:6px;font-size:13px;font-family:monospace;white-space:pre-wrap;';
    el.className = 'trial-flow__notice';
    var detail = arguments.length > 2 ? ': ' + (err && err.message ? err.message : err) : '';
    el.textContent = '[trial-flow debug] ' + label + detail;
    root.insertBefore(el, root.firstChild);
  }

  function isThemeEditor() {
    if (!window.Shopify) return false;
    if (window.Shopify.designMode) return true;
    // Covers Online Store > Themes > Preview (and that preview link
    // opened on any device/browser): Shopify.theme.role is 'main' only
    // on the live/published theme, so this is false there and true for
    // any unpublished, preview, or demo theme.
    return !!(window.Shopify.theme && window.Shopify.theme.role !== 'main');
  }

  /**
   * Theme Preview (Online Store > Themes > ... > Preview, and shared
   * preview links) pins the unpublished theme using query params on the
   * URL. A plain window.location.href = '/pages/...' drops them, so the
   * next step of the funnel loads on the PUBLISHED theme instead - which
   * is why the flow dead-ends or 404s in preview while working fine on
   * the live theme. go() carries those params across every hop.
   */
  var PREVIEW_PARAMS = ['preview_theme_id', 'key', '_ab', '_fd', '_sc', 'pb'];

  function withPreview(path) {
    try {
      var current = new URLSearchParams(window.location.search);
      var carried = [];
      PREVIEW_PARAMS.forEach(function (name) {
        var value = current.get(name);
        if (value !== null) {
          carried.push(encodeURIComponent(name) + '=' + encodeURIComponent(value));
        }
      });
      if (!carried.length) return path;
      return path + (path.indexOf('?') === -1 ? '?' : '&') + carried.join('&');
    } catch (e) {
      return path;
    }
  }

  function go(path) {
    window.location.href = withPreview(path);
  }

  function showEditorNotice(root, message) {
    root.querySelectorAll('.trial-flow__notice').forEach(function (n) { n.remove(); });
    var el = document.createElement('div');
    el.style.cssText = 'background:#edf1e9;border:1px solid #cdd8c8;color:#5e7357;' +
      'padding:14px 18px;margin:16px 0;border-radius:8px;font-size:14px;line-height:1.5;';
    el.className = 'trial-flow__notice';
    el.textContent = message;
    root.insertBefore(el, root.firstChild);
  }

  /* ============================= STEP 1 ============================= */

  /* ============================ GALLERY ============================ */

  /**
   * Step 1 image gallery. The thumbnails were being rendered by
   * main-product-trial.liquid but nothing ever listened for a click on
   * them, so the main image never changed. Each thumb carries the
   * full-size src/srcset/alt of its own media in data attributes, so
   * swapping is a straight attribute copy with no network lookup.
   */
  function initGallery() {
    var root = document.querySelector('[data-trial-step1]');
    if (!root || root.getAttribute('data-tf-gallery-init') === '1') return;
    root.setAttribute('data-tf-gallery-init', '1');

    try {
      var mainImg = root.querySelector('#TrialMainImage');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-thumb]'));
      if (!mainImg || thumbs.length === 0) return;

      function activate(thumb) {
        var src = thumb.getAttribute('data-full-src');
        if (!src) return;

        var srcset = thumb.getAttribute('data-full-srcset');
        if (srcset) {
          mainImg.setAttribute('srcset', srcset);
        } else {
          mainImg.removeAttribute('srcset');
        }
        mainImg.setAttribute('src', src);
        mainImg.setAttribute('alt', thumb.getAttribute('data-full-alt') || '');
        mainImg.setAttribute('loading', 'eager');

        thumbs.forEach(function (t) {
          var on = t === thumb;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          activate(thumb);
        });

        thumb.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = thumbs[(i + (e.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length];
          next.focus();
          activate(next);
        });
      });

      // If nothing is marked active (e.g. the featured media isn't in the
      // media list), fall back to the first thumb so the state is never empty.
      if (!root.querySelector('[data-thumb].is-active')) {
        thumbs[0].classList.add('is-active');
        thumbs[0].setAttribute('aria-pressed', 'true');
      }
    } catch (err) {
      showDebug(root, 'Image gallery failed to initialise', err);
    }
  }

  function initStep1() {
    var root = document.querySelector('[data-trial-step1]');
    if (!root || root.getAttribute('data-tf-init') === '1') return;
    root.setAttribute('data-tf-init', '1');

    try {
      var dataEl = root.querySelector('[data-trial-variants]');
      if (!dataEl) throw new Error('data-trial-variants script tag not found in the DOM');

      var data = JSON.parse(dataEl.textContent);
      var trialHandle = root.getAttribute('data-trial-handle');
      var pantsHandle = root.getAttribute('data-nappy-pants-handle');

      var optionPositions = {};
      root.querySelectorAll('[data-option-position]').forEach(function (group) {
        var pos = group.getAttribute('data-option-position');
        var select = group.querySelector('[data-option-select]');
        if (select) optionPositions[pos] = select.value;
      });

      function findMatchingVariant() {
        return data.variants.find(function (v) {
          return v.options.every(function (val, idx) {
            return optionPositions[idx + 1] === val;
          });
        });
      }

      function render() {
        var variant = findMatchingVariant();
        var priceNow = root.querySelector('[data-trial-price] .trial-flow__price-now');
        var priceWas = root.querySelector('[data-trial-price] .trial-flow__price-was');
        var cta = root.querySelector('[data-trial-get-started]');
        if (!variant) {
          cta.disabled = true;
          showDebug(root, 'Get started disabled - no variant matches the current selection ' +
            JSON.stringify(optionPositions) + '. Available combinations: ' +
            data.variants.map(function (v) { return v.title; }).join(', ') +
            '. This usually means an option value in the dropdown doesn\'t exactly match a real variant (e.g. this product\'s option order/spelling changed) - open this product in Shopify admin and confirm a variant exists for every dropdown combination.');
          return;
        }

        priceNow.textContent = formatMoney(variant.price);
        if (priceWas) {
          if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
            priceWas.textContent = formatMoney(variant.compareAtPrice);
            priceWas.hidden = false;
          } else {
            priceWas.hidden = true;
          }
        }

        var hasPlans = variant.sellingPlans && variant.sellingPlans.length > 0;
        var isOutOfStock = variant.available === false;
        // Previously gated on hasPlans only, since raw stock availability
        // was seen disagreeing with Shopify's own Admin API shortly after
        // a product was freshly published, incorrectly blocking checkout.
        // Now also grey out/disable on genuine out-of-stock, per explicit
        // request - if that false-positive resurfaces, check "Continue
        // selling when out of stock" on the affected variant first.
        cta.disabled = !hasPlans || isOutOfStock;
        cta.classList.toggle('trial-flow__cta--oos', isOutOfStock);

        if (isOutOfStock) {
          // Expected, normal state now (not a bug) - no debug banner,
          // just remove any leftover banner from a previous selection.
          cta.textContent = 'Out of stock';
          root.querySelectorAll('.trial-flow__notice').forEach(function (n) { n.remove(); });
        } else if (!hasPlans) {
          cta.textContent = 'Get started';
          showDebug(root, 'Get started disabled - no selling plans found on variant "' + variant.title + '" (id ' + variant.id + '). Check the selling plan group is attached to this exact variant in Appstle/Shopify admin, and that this page isn\'t showing a cached copy (hard refresh).');
        } else {
          cta.textContent = 'Get started';
        }
      }

      if (pantsHandle) {
        root.querySelectorAll('[data-type-toggle]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.getAttribute('data-type') === 'pants') {
              go('/products/' + pantsHandle);
            } else {
              root.querySelectorAll('[data-type-toggle]').forEach(function (b) { b.classList.remove('is-selected'); });
              btn.classList.add('is-selected');
            }
          });
        });
      }

      root.querySelectorAll('[data-option-select]').forEach(function (select) {
        select.addEventListener('change', function () {
          optionPositions[select.getAttribute('data-option-position')] = select.value;
          render();
        });
      });

      root.querySelector('[data-trial-get-started]').addEventListener('click', function () {
        var variant = findMatchingVariant();
        if (!variant || !variant.sellingPlans || variant.sellingPlans.length === 0) return;
        if (variant.available === false) return;

        setState({
          trialProductHandle: trialHandle,
          trialProductTitle: data.trialProductTitle,
          trialProductImage: data.trialProductImage,
          trialDeliveryPrice: data.trialDeliveryPrice,
          subscriptionDisplay: data.subscriptionDisplay,
          variantId: variant.id,
          variantTitle: variant.title,
          variantPrice: variant.price,
          variantCompareAtPrice: variant.compareAtPrice,
          sellingPlans: variant.sellingPlans
        });

        go('/pages/choose-subscription');
      });

      render();
    } catch (err) {
      showDebug(root, 'Step 1 failed to initialise', err);
    }
  }

  /* ============================= STEP 2 ============================= */

  function initStep2() {
    var root = document.querySelector('[data-trial-step2]');
    if (!root || root.getAttribute('data-tf-init') === '1') return;
    root.setAttribute('data-tf-init', '1');

    try {
      var state = getState();
      if (!state.variantId || !state.sellingPlans || state.sellingPlans.length === 0) {
        if (isThemeEditor()) {
          showEditorNotice(root, 'This step needs a real trial selection carried over from Step 1 to preview live pricing. It’s safe to add or edit blocks here — go through Step 1 on the storefront first (starting from the trial product page), then continue to this step in the same tab, to see it populated with real data.');
          return;
        }
        window.location.href = '/';
        return;
      }

      var firstDispatchDays = parseInt(root.getAttribute('data-first-dispatch-days'), 10) || 10;
      var dispatchDate = new Date();
      dispatchDate.setDate(dispatchDate.getDate() + firstDispatchDays);
      var dispatchLabel = formatDate(dispatchDate);

      var dispatchEl = root.querySelector('[data-step2-dispatch-date]');
      if (dispatchEl) dispatchEl.textContent = dispatchLabel;

      var headingEl = root.querySelector('[data-step2-heading]');
      if (headingEl && state.trialProductTitle) {
        headingEl.textContent = 'You\u2019ve selected our ' + state.trialProductTitle + '!';
      }

      var select = root.querySelector('[data-frequency-select]');
      select.innerHTML = '';
      state.sellingPlans.forEach(function (plan) {
        var opt = document.createElement('option');
        opt.value = plan.id;
        opt.textContent = plan.name;
        select.appendChild(opt);
      });

      function renderCard() {
        var plan = state.sellingPlans.find(function (p) { return String(p.id) === String(select.value); });
        if (!plan) return;

        var display = state.subscriptionDisplay || {};
        var badge = root.querySelector('[data-sub-badge]');
        var mediaEl = root.querySelector('[data-sub-media]');
        var titleEl = root.querySelector('[data-sub-title]');
        var metaEl = root.querySelector('[data-sub-meta]');
        var comparePriceEl = root.querySelector('[data-sub-compare-price]');
        var priceEl = root.querySelector('[data-sub-price]');
        var deliveryEl = root.querySelector('[data-sub-delivery]');

        var image = display.image || state.trialProductImage;
        if (image) {
          mediaEl.innerHTML = '<img src="' + image + '" alt="' + (display.title || state.trialProductTitle) + '" width="120">';
        }
        titleEl.textContent = display.title || state.trialProductTitle;
        metaEl.textContent = [state.variantTitle, display.packNote].filter(Boolean).join(' · ');

        /* Resolve the real Appstle price schedule from the selling plan's
           own price_adjustments (e.g. cycle 1 free, cycles 2-4 at a
           discounted flat price, cycle 5+ at the standard price) rather
           than a single flat number. Only "price" (fixed price) tiers can
           be resolved here without knowing which base price percentage/
           fixed_amount tiers apply against - those fall back below. */
        var schedule = getPriceSchedule(plan);
        var ongoingPrice, scheduleNote;
        if (schedule) {
          ongoingPrice = schedule.firstPrice;
          scheduleNote = schedule.note;
        } else {
          ongoingPrice = (display.price && display.price > 0) ? display.price : plan.price;
          scheduleNote = '';
        }
        priceEl.textContent = formatMoney(ongoingPrice);

        var scheduleNoteEl = root.querySelector('[data-sub-schedule-note]');
        if (scheduleNoteEl) {
          if (scheduleNote) {
            scheduleNoteEl.textContent = scheduleNote;
            scheduleNoteEl.hidden = false;
          } else {
            scheduleNoteEl.hidden = true;
          }
        }

        var compareAt = display.compareAtPrice;
        if (compareAt && compareAt > ongoingPrice) {
          comparePriceEl.textContent = formatMoney(compareAt);
          comparePriceEl.hidden = false;
          var pct = Math.round((1 - ongoingPrice / compareAt) * 100);
          badge.textContent = 'Save up to ' + pct + '%';
          badge.hidden = false;
        } else {
          comparePriceEl.hidden = true;
          badge.hidden = true;
        }
        deliveryEl.textContent = 'Delivery: Free';

        root._activeSelection = { sellingPlanId: plan.id, frequencyLabel: plan.name, subscriptionPrice: ongoingPrice, subscriptionCompareAtPrice: (compareAt && compareAt > ongoingPrice) ? compareAt : 0, subscriptionScheduleNote: scheduleNote };
      }

      select.addEventListener('change', renderCard);
      renderCard();

      root.querySelector('[data-summary-trial-price]').textContent = formatMoney(state.variantPrice);
      root.querySelector('[data-summary-delivery-price]').textContent = formatMoney(state.trialDeliveryPrice);

      root.querySelector('[data-review-order]').addEventListener('click', function () {
        var sel = root._activeSelection;
        if (!sel) return;

        setState({
          sellingPlanId: sel.sellingPlanId,
          frequencyLabel: sel.frequencyLabel,
          subscriptionPrice: sel.subscriptionPrice,
          subscriptionCompareAtPrice: sel.subscriptionCompareAtPrice,
          subscriptionScheduleNote: sel.subscriptionScheduleNote,
          nextDispatchDate: dispatchLabel
        });

        go('/pages/review-order');
      });
    } catch (err) {
      showDebug(root, 'Step 2 failed to initialise', err);
    }
  }

  /* ============================= STEP 3 ============================= */

  function initStep3() {
    var root = document.querySelector('[data-trial-step3]');
    if (!root || root.getAttribute('data-tf-init') === '1') return;
    root.setAttribute('data-tf-init', '1');

    try {
      var state = getState();
      if (!state.variantId || !state.sellingPlanId) {
        if (isThemeEditor()) {
          showEditorNotice(root, 'This page needs a real trial + subscription selection carried over from Steps 1-2 to preview live pricing. It’s safe to add or edit blocks here (e.g. upsell products below) — go through Steps 1 and 2 on the storefront first, in the same tab, to see it populated with real data.');
          return;
        }
        window.location.href = '/';
        return;
      }

      var backBtn = root.querySelector('[data-review-back]');
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          if (document.referrer && document.referrer.indexOf(window.location.host) !== -1) {
            window.history.back();
          } else {
            go('/pages/choose-subscription');
          }
        });
      }

      if (state.trialProductImage) {
        setMediaImage(root.querySelector('[data-trial-media]'), state.trialProductImage, state.trialProductTitle);
      }
      root.querySelector('[data-trial-title]').textContent = state.trialProductTitle;
      root.querySelector('[data-trial-meta]').textContent = state.variantTitle;
      var wasEl = root.querySelector('[data-trial-was-price]');
      if (state.variantCompareAtPrice && state.variantCompareAtPrice > state.variantPrice) {
        wasEl.textContent = formatMoney(state.variantCompareAtPrice);
        wasEl.hidden = false;
      }
      root.querySelector('[data-trial-now-price]').textContent = formatMoney(state.variantPrice);
      root.querySelector('[data-review-delivery-price]').textContent = formatMoney(state.trialDeliveryPrice);
      root.querySelector('[data-review-today-total]').textContent = formatMoney(state.trialDeliveryPrice);
      root.querySelector('[data-grand-today-total]').textContent = formatMoney(state.trialDeliveryPrice);

      var display = state.subscriptionDisplay || {};
      var subImage = display.image || state.trialProductImage;
      var subMediaEl = root.querySelector('[data-sub-review-media]');
      if (subImage && subMediaEl) {
        setMediaImage(subMediaEl, subImage, display.title || state.trialProductTitle);
      }
      root.querySelector('[data-sub-review-title]').textContent = display.title || state.trialProductTitle;
      root.querySelector('[data-sub-review-meta]').textContent = [state.variantTitle, display.packNote].filter(Boolean).join(' · ');

      var subWasEl = root.querySelector('[data-sub-review-was-price]');
      var subCompareAt = state.subscriptionCompareAtPrice > 0 ? state.subscriptionCompareAtPrice : display.compareAtPrice;
      if (subCompareAt && subCompareAt > state.subscriptionPrice) {
        subWasEl.textContent = formatMoney(subCompareAt);
        subWasEl.hidden = false;
      }
      root.querySelector('[data-sub-review-price]').textContent = formatMoney(state.subscriptionPrice);
      root.querySelector('[data-review-sub-total]').textContent = formatMoney(state.subscriptionPrice);

      var reviewScheduleNoteEl = root.querySelector('[data-sub-review-schedule-note]');
      if (reviewScheduleNoteEl) {
        if (state.subscriptionScheduleNote) {
          reviewScheduleNoteEl.textContent = state.subscriptionScheduleNote;
          reviewScheduleNoteEl.hidden = false;
        } else {
          reviewScheduleNoteEl.hidden = true;
        }
      }

      root.querySelector('[data-review-frequency]').textContent = state.frequencyLabel;
      root.querySelector('[data-review-ships-note]').textContent =
        'Ships and charges on ' + state.nextDispatchDate + '.';

      root.querySelector('[data-complete-order]').addEventListener('click', function (e) {
        var btn = e.currentTarget;
        btn.disabled = true;
        var errorEl = root.querySelector('[data-review-error]');
        errorEl.hidden = true;

        // Tag the trial line so a repeat visit can find and replace it -
        // this is a hidden Shopify line item property (underscore prefix
        // keeps it out of the cart/checkout UI), not anything visible to
        // the customer.
        var items = [
          {
            id: state.variantId,
            quantity: 1,
            selling_plan: state.sellingPlanId,
            properties: { '_trial_flow': 'true' }
          }
        ];

        root.querySelectorAll('[data-upsell-checkbox]:checked').forEach(function (checkbox) {
          items.push({ id: parseInt(checkbox.value, 10), quantity: 1 });
        });

        /**
         * Only one free trial pack is ever meant to be in the cart. Shopify's
         * /cart/add.js merges quantities when the same variant + selling plan
         * is added twice - and since the quantity stepper is hidden on this
         * flow, a customer who runs it, abandons at checkout, then comes back
         * and runs it again previously ended up with 2 (or more) trial packs
         * with no visible way to fix it themselves.
         *
         * Matched by product HANDLE (state.trialProductHandle, captured back
         * in Step 1), not the _trial_flow line property below. A property
         * only identifies lines added by THIS version of the script - it
         * can't see trial lines already sitting in the cart from earlier
         * testing/older code, so relying on it alone leaves old duplicates
         * behind forever. Handle matching catches every existing line for
         * this product regardless of when or how it was added, then removes
         * all of them before the fresh one goes in.
         */
        // Removes each stale trial line ONE AT A TIME, in sequence -
        // Shopify's /cart/change.js mutates the whole cart per request, so
        // firing several removals in parallel (the previous version used
        // Promise.all) can race: two responses can each be computed against
        // the same starting cart state and the server keeps only the last
        // one to land, silently dropping the other removal. That let
        // duplicate trial lines survive. A plain reduce-based chain forces
        // each change to wait for the previous one's response before firing.
        function removeLinesSequentially(lines) {
          return lines.reduce(function (chain, line) {
            return chain.then(function () {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: 0 })
              }).then(function (res) {
                if (!res.ok) {
                  return res.json().then(function (err) {
                    throw new Error('Removing previous trial line failed: ' + JSON.stringify(err));
                  });
                }
                return res.json();
              });
            });
          }, Promise.resolve());
        }

        fetch('/cart.js')
          .then(function (res) {
            if (!res.ok) throw new Error('Could not read current cart (status ' + res.status + ')');
            return res.json();
          })
          .then(function (cart) {
            var previousTrialLines = cart.items.filter(function (line) {
              return line.handle === state.trialProductHandle;
            });
            return removeLinesSequentially(previousTrialLines);
          })
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: items })
            });
          })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (err) { throw err; });
            return res.json();
          })
          .then(function () {
            // Don't clear the saved state here - the customer may hit
            // Back from checkout (or abandon it) and land back on this
            // review page, and it should still show what they picked
            // rather than losing everything and showing "no selection"
            // (or, worse, bouncing them out with nothing to review).
            // sessionStorage is tab-scoped and short-lived anyway, so
            // leaving it set here does no harm.
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            btn.disabled = false;
            console.error('[trial-flow] Complete order failed', err);
            errorEl.textContent = (err && err.description) || (err && err.message) ||
              'Something went wrong adding your order. Please try again.';
            errorEl.hidden = false;
          });
      });
    } catch (err) {
      showDebug(root, 'Step 3 failed to initialise', err);
    }
  }

  /* ========================== SIZE GUIDE POPUP ========================== */

  function initSizeGuide() {
    var modal = document.querySelector('[data-size-guide-modal]');
    var openBtn = document.querySelector('[data-size-guide-open]');
    if (!modal || !openBtn || modal.getAttribute('data-tf-init') === '1') return;
    modal.setAttribute('data-tf-init', '1');

    function open() {
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    function close() {
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', open);
    modal.querySelectorAll('[data-size-guide-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* ============================= STEP NAV ============================= */
  /* Makes completed steps in the header progress track clickable, like the
     Kit & Kin breadcrumb — lets someone jump back to a step they already
     finished. Only "is-done" steps are wired up; the current step isn't
     a link since you're already there. */

  var stepNavBound = false;

  function initStepNav() {
    if (stepNavBound) return;
    stepNavBound = true;
    var state = getState();
    var items = document.querySelectorAll('.trial-steps__item.is-done');

    items.forEach(function (item) {
      var target = item.getAttribute('data-step-target');
      var href = null;

      if (target === 'select') {
        href = state.trialProductHandle ? withPreview('/products/' + state.trialProductHandle) : '/';
      } else if (target === 'subscribe') {
        href = withPreview('/pages/choose-subscription');
      }

      if (!href) return;

      item.setAttribute('role', 'link');
      item.setAttribute('tabindex', '0');
      item.addEventListener('click', function () { window.location.href = href; });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.location.href = href;
        }
      });
    });
  }

  function initAll() {
    initGallery();
    initStep1();
    initStep2();
    initStep3();
    initStepNav();
    initSizeGuide();
  }

  document.addEventListener('DOMContentLoaded', initAll);

  /**
   * In Online Store > Customize, changing any setting makes Shopify
   * replace the section's HTML and fire shopify:section:load.
   * DOMContentLoaded does NOT fire again, and the <script src> tag that
   * sits inside the section is not re-executed when it's re-inserted -
   * so without this listener every handler bound above is lost after the
   * very first edit. The section still LOOKS fine, but the thumbnails,
   * the size dropdown and Get started all stop responding, which is the
   * classic "works on the live site, dead in the editor" symptom.
   *
   * The data-tf-init guards above make this safe to call repeatedly:
   * freshly rendered markup has no flag, untouched markup is skipped.
   */
  document.addEventListener('shopify:section:load', initAll);
})();
 